// SuccessDialogProvider.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Box,
  Fade,
  Zoom,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

const SuccessDialogContext = createContext(null);
export const useSuccessDialog = () => useContext(SuccessDialogContext);

export function SuccessDialogProvider({ children }) {
  const [successInfo, setSuccessInfo] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const showSuccess = useCallback(
    (
      message,
      shouldReload = false,
      fallback = "Operation completed successfully"
    ) => {
      let displayMessage = fallback;

      if (typeof message === "string") {
        displayMessage = message;
      } else if (
        typeof message === "object" &&
        message !== null &&
        "response" in message
      ) {
        const response = message.response;
        displayMessage =
          response?.data?.message || response?.statusText || fallback;
      }

      setSuccessInfo({ message: displayMessage, shouldReload });
      setIsDialogOpen(true);
    },
    []
  );

  const handleClose = () => {
    if (successInfo?.shouldReload) {
      window.location.reload();
    }
    setIsDialogOpen(false);
  };

  const handleExited = () => {
    if (successInfo?.shouldReload) {
      window.location.reload();
    }
    setSuccessInfo(null);
  };

  return (
    <SuccessDialogContext.Provider value={showSuccess}>
      {children}
      <SuccessDialog
        open={isDialogOpen}
        onClose={handleClose}
        onExited={handleExited}
        info={successInfo}
      />
    </SuccessDialogContext.Provider>
  );
}

function SuccessDialog({ open, onClose, onExited, info }) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      maxWidth='sm'
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <Box>
          <DialogTitle
            sx={{
              bgcolor: "success.main",
              color: "primary.contrastText",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
              py: 2,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)",
              },
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Zoom in={open} style={{ transitionDelay: "100ms" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "white" }} />
                </Box>
              </Zoom>
              <Typography
                variant='h6'
                color='white'
                fontWeight='600'
                sx={{
                  letterSpacing: "0.5px",
                  fontSize: "1.1rem",
                }}
              >
                Success
              </Typography>
            </Stack>
            <IconButton
              onClick={onClose}
              size='small'
              sx={{
                color: "white",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 3,
              bgcolor: "background.paper",
              position: "relative",
              maxHeight: "50vh",
              overflowY: "auto",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.2) 50%, rgba(76, 175, 80, 0.1) 100%)",
              },
            }}
          >
            <Fade in={open} timeout={500} style={{ transitionDelay: "200ms" }}>
              <Typography
                variant='body1'
                sx={{
                  color: "text.primary",
                  lineHeight: 1.6,
                  fontSize: "1rem",
                  fontWeight: 500,
                  whiteSpace: "pre-line",
                }}
              >
                {info?.message ?? "Operation completed successfully."}
              </Typography>
            </Fade>
          </DialogContent>
        </Box>
      </Fade>
    </Dialog>
  );
}
